import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { apiJson, formatApiErrorForDisplay } from '@/lib/api';
import { track } from '@/lib/analytics';
import { OutreachReplyAttachmentPicker } from '@/components/admin/OutreachReplyAttachmentPicker';
import { sendOutreachReply } from '@/lib/outreachReplySend';
import { AdminUnreadDot } from '@/components/admin/AdminUnreadDot';
import { usePressOutreachUnreadReplies } from '@/hooks/usePressOutreachUnreadReplies';
import { PRESS_OUTREACH_REPLIES_QUERY_KEY } from '@/lib/pressOutreachRepliesStorage';

const DRAFTS_KEY = ['admin', 'press-outreach-drafts'] as const;
const SENDS_KEY = ['admin', 'press-outreach-sends'] as const;

function parseDetail(err: unknown): string {
  return formatApiErrorForDisplay(err);
}

const SEND_EVENT_BADGE_CLASSES: Record<string, string> = {
  delivered: 'bg-green-100 text-green-800',
  opened: 'bg-blue-100 text-blue-800',
  clicked: 'bg-emerald-100 text-emerald-900',
  bounced: 'bg-red-100 text-red-800',
  complained: 'bg-red-100 text-red-900',
  failed: 'bg-red-100 text-red-800',
  sent: 'bg-nova-muted text-nova-foreground',
};

function sendEventBadgeClass(event: string | null): string {
  if (!event) return 'bg-nova-muted text-nova-muted-foreground';
  return (
    SEND_EVENT_BADGE_CLASSES[event.toLowerCase()] ?? 'bg-nova-muted text-nova-foreground'
  );
}

function dedupeReplySubject(subject: string): string {
  const base = (subject ?? '').trim();
  if (!base) return 'Re:';
  if (/^(re|fwd|fw):/i.test(base)) return base;
  return `Re: ${base}`;
}

function formatTimestamp(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function formatRelative(value: string | null): string {
  if (!value) return '—';
  const ts = new Date(value).getTime();
  if (Number.isNaN(ts)) return value;
  const diffMs = Date.now() - ts;
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(value).toLocaleDateString();
}

type PressDraftRow = {
  contact_id: number;
  outlet_key: string;
  outlet_name: string;
  email: string | null;
  article_url: string | null;
  pitch_url: string | null;
  notes: string | null;
  contact_status: string;
  email_id: number | null;
  email_key: string;
  email_status: string | null;
  subject: string | null;
  body: string | null;
  sent_at: string | null;
  updated_at: string | null;
  last_event: string | null;
  default_subject: string;
  default_body: string;
};

type PressDraftListResponse = {
  rows: PressDraftRow[];
  from_email: string;
  reply_to: string | null;
};

type PressReplyContactRef = {
  id: number;
  outlet_name: string;
  email: string | null;
  status: string;
};

type PressReplyRow = {
  id: string;
  from_email: string;
  to_emails: string[];
  subject: string;
  received_at: string | null;
  message_id: string | null;
  has_attachments: boolean;
  matched_contact: PressReplyContactRef | null;
  manual_reply_sent_at: string | null;
};

type PressRepliesListResponse = {
  replies: PressReplyRow[];
  has_more: boolean;
  fetched_at: string;
  error: string | null;
};

type PressReplyDetailResponse = {
  id: string;
  from_email: string;
  to_emails: string[];
  cc: string[];
  bcc: string[];
  reply_to: string[];
  subject: string;
  received_at: string | null;
  message_id: string | null;
  html: string | null;
  text: string | null;
  attachments: Array<{
    id?: string;
    filename?: string;
    content_type?: string;
  }>;
  matched_contact: PressReplyContactRef | null;
  manual_reply_sent_at: string | null;
};

type PressReplySendRequest = {
  subject: string;
  body: string;
  include_quoted_original: boolean;
  files: File[];
};

type PressReplySendResponse = {
  message_id: string;
  from_email: string;
  to_email: string;
  subject: string;
  sent_at: string;
  contact_status_after: string | null;
  persisted_email_id: number | null;
  attachment_filenames?: string[];
};

type PressSendRow = {
  id: string;
  from_email: string;
  to_emails: string[];
  subject: string;
  sent_at: string | null;
  last_event: string | null;
  matched_contact: PressReplyContactRef | null;
};

type PressSendsListResponse = {
  sends: PressSendRow[];
  has_more: boolean;
  fetched_at: string;
  error: string | null;
};

type PressSendDetailResponse = {
  id: string;
  from_email: string;
  to_emails: string[];
  reply_to: string[];
  subject: string;
  sent_at: string | null;
  last_event: string | null;
  html: string | null;
  text: string | null;
  matched_contact: PressReplyContactRef | null;
};

type DraftEdits = {
  email: string;
  subject: string;
  body: string;
};

function PressOutreachPage() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const screenTracked = useRef(false);
  const [edits, setEdits] = useState<Record<number, DraftEdits>>({});
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(
    null
  );
  const [openReplyId, setOpenReplyId] = useState<string | null>(null);
  const [openReplyMode, setOpenReplyMode] = useState<'html' | 'text'>('html');
  const [openSendId, setOpenSendId] = useState<string | null>(null);
  const [openSendMode, setOpenSendMode] = useState<'html' | 'text'>('html');

  useEffect(() => {
    if (screenTracked.current) return;
    screenTracked.current = true;
    track('Press Outreach Screen');
  }, []);

  const draftsQuery = useQuery({
    queryKey: DRAFTS_KEY,
    queryFn: () =>
      apiJson<PressDraftListResponse>(
        getToken,
        '/v1/admin/press-outreach/drafts'
      ),
  });

  const repliesQuery = useQuery({
    queryKey: PRESS_OUTREACH_REPLIES_QUERY_KEY,
    queryFn: () =>
      apiJson<PressRepliesListResponse>(
        getToken,
        '/v1/admin/press-outreach/replies?limit=50'
      ),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const { hasUnread: hasUnreadReplies, markRepliesOpened } =
    usePressOutreachUnreadReplies(repliesQuery.data?.replies);

  const sendsQuery = useQuery({
    queryKey: SENDS_KEY,
    queryFn: () =>
      apiJson<PressSendsListResponse>(
        getToken,
        '/v1/admin/press-outreach/sends?limit=50'
      ),
  });

  const replyDetailQuery = useQuery({
    queryKey: ['admin', 'press-outreach-reply', openReplyId] as const,
    queryFn: () =>
      apiJson<PressReplyDetailResponse>(
        getToken,
        `/v1/admin/press-outreach/replies/${encodeURIComponent(openReplyId ?? '')}`
      ),
    enabled: !!openReplyId,
  });

  const sendDetailQuery = useQuery({
    queryKey: ['admin', 'press-outreach-send', openSendId] as const,
    queryFn: () =>
      apiJson<PressSendDetailResponse>(
        getToken,
        `/v1/admin/press-outreach/sends/${encodeURIComponent(openSendId ?? '')}`
      ),
    enabled: !!openSendId,
  });

  const saveDraftMutation = useMutation({
    mutationFn: (vars: {
      contact_id: number;
      email: string;
      subject: string;
      body: string;
    }) =>
      apiJson(getToken, '/v1/admin/press-outreach/drafts', {
        method: 'POST',
        body: JSON.stringify(vars),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DRAFTS_KEY });
      setBanner({ ok: true, text: 'Draft saved.' });
      track('Press Outreach Draft Saved');
    },
    onError: (err) => setBanner({ ok: false, text: parseDetail(err) }),
  });

  const sendMutation = useMutation({
    mutationFn: (vars: {
      contact_id: number;
      email: string;
      subject: string;
      body: string;
    }) =>
      apiJson(getToken, '/v1/admin/press-outreach/send', {
        method: 'POST',
        body: JSON.stringify(vars),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DRAFTS_KEY });
      queryClient.invalidateQueries({ queryKey: SENDS_KEY });
      setBanner({ ok: true, text: 'Sent.' });
      track('Press Outreach Email Sent');
    },
    onError: (err) => setBanner({ ok: false, text: parseDetail(err) }),
  });

  const sendReplyMutation = useMutation({
    mutationFn: (vars: { replyId: string; payload: PressReplySendRequest }) =>
      sendOutreachReply<PressReplySendResponse>(
        getToken,
        `/v1/admin/press-outreach/replies/${encodeURIComponent(vars.replyId)}/send`,
        vars.payload
      ),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: ['admin', 'press-outreach-reply', vars.replyId],
      });
      queryClient.invalidateQueries({
        queryKey: PRESS_OUTREACH_REPLIES_QUERY_KEY,
      });
      queryClient.invalidateQueries({ queryKey: SENDS_KEY });
      queryClient.invalidateQueries({ queryKey: DRAFTS_KEY });
      track('Press Outreach Reply Sent');
    },
  });

  const rows = draftsQuery.data?.rows ?? [];
  useEffect(() => {
    if (!rows.length) return;
    setEdits((prev) => {
      const next = { ...prev };
      for (const row of rows) {
        if (next[row.contact_id]) continue;
        next[row.contact_id] = {
          email: row.email ?? '',
          subject: row.subject ?? row.default_subject,
          body: row.body ?? row.default_body,
        };
      }
      return next;
    });
  }, [rows]);

  return (
    <div className='space-y-4'>
      <div>
        <h1 className='text-2xl font-semibold text-nova-foreground'>Press Outreach</h1>
        <p className='mt-1 text-sm text-nova-muted-foreground'>
          Tiny gym for the two list-update pitches that can actually put Harbor
          in front of people searching for a co-parenting app. Copy is
          prefilled and editable. Send is immediate — no Celery queue.
        </p>
        {draftsQuery.data ? (
          <p className='mt-2 text-xs text-nova-muted-foreground'>
            From {draftsQuery.data.from_email}
            {draftsQuery.data.reply_to
              ? ` · Reply-To ${draftsQuery.data.reply_to}`
              : ''}
          </p>
        ) : null}
      </div>

      {banner ? (
        <div
          className={`rounded-lg border px-4 py-3 text-sm ${
            banner.ok
              ? 'bg-green-50 border-green-200 text-green-900'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {banner.text}
        </div>
      ) : null}

      <section className='space-y-4'>
        <h2 className='text-lg font-semibold text-nova-foreground'>Email gym</h2>
        {draftsQuery.isLoading ? (
          <p className='text-sm text-nova-muted-foreground'>Loading drafts…</p>
        ) : draftsQuery.isError ? (
          <p className='text-sm text-red-600'>
            {parseDetail(draftsQuery.error)}
          </p>
        ) : (
          <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
            {rows.map((row) => {
              const edit = edits[row.contact_id] ?? {
                email: row.email ?? '',
                subject: row.subject ?? row.default_subject,
                body: row.body ?? row.default_body,
              };
              const alreadySent = Boolean(row.sent_at);
              const canSend = edit.email.trim().length > 0 && !alreadySent;
              return (
                <article
                  key={row.contact_id}
                  className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4 space-y-3'
                >
                  <div className='flex items-start justify-between gap-3'>
                    <div>
                      <h3 className='text-base font-semibold text-nova-foreground'>
                        {row.outlet_name}
                      </h3>
                      <p className='text-xs text-nova-muted-foreground'>
                        {row.contact_status}
                        {row.email_status ? ` · ${row.email_status}` : ''}
                        {row.last_event ? ` · ${row.last_event}` : ''}
                      </p>
                    </div>
                    {alreadySent ? (
                      <span className='inline-flex px-2 py-0.5 rounded-md bg-green-100 text-green-800 text-[11px] font-semibold uppercase tracking-wide'>
                        Sent {formatRelative(row.sent_at)}
                      </span>
                    ) : (
                      <span className='inline-flex px-2 py-0.5 rounded-md bg-amber-100 text-amber-900 text-[11px] font-semibold uppercase tracking-wide'>
                        Draft
                      </span>
                    )}
                  </div>

                  {row.notes ? (
                    <p className='text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2'>
                      {row.notes}
                    </p>
                  ) : null}

                  <div className='flex flex-wrap gap-3 text-xs'>
                    {row.article_url ? (
                      <a
                        href={row.article_url}
                        target='_blank'
                        rel='noreferrer'
                        className='text-nova-primary underline'
                      >
                        List to update
                      </a>
                    ) : null}
                    {row.pitch_url ? (
                      <a
                        href={row.pitch_url}
                        target='_blank'
                        rel='noreferrer'
                        className='text-nova-primary underline'
                      >
                        How to pitch
                      </a>
                    ) : null}
                  </div>

                  <label className='block text-sm'>
                    <span className='font-semibold text-nova-muted-foreground'>To</span>
                    <input
                      type='email'
                      value={edit.email}
                      disabled={alreadySent}
                      onChange={(event) =>
                        setEdits((prev) => ({
                          ...prev,
                          [row.contact_id]: {
                            ...edit,
                            email: event.target.value,
                          },
                        }))
                      }
                      placeholder='editor@example.com'
                      className='mt-1 w-full rounded-md border border-nova-input px-3 py-2 text-sm disabled:bg-nova-muted'
                    />
                  </label>

                  <label className='block text-sm'>
                    <span className='font-semibold text-nova-muted-foreground'>Subject</span>
                    <input
                      type='text'
                      value={edit.subject}
                      disabled={alreadySent}
                      onChange={(event) =>
                        setEdits((prev) => ({
                          ...prev,
                          [row.contact_id]: {
                            ...edit,
                            subject: event.target.value,
                          },
                        }))
                      }
                      className='mt-1 w-full rounded-md border border-nova-input px-3 py-2 text-sm disabled:bg-nova-muted'
                    />
                  </label>

                  <label className='block text-sm'>
                    <span className='font-semibold text-nova-muted-foreground'>Message</span>
                    <textarea
                      rows={16}
                      value={edit.body}
                      disabled={alreadySent}
                      onChange={(event) =>
                        setEdits((prev) => ({
                          ...prev,
                          [row.contact_id]: {
                            ...edit,
                            body: event.target.value,
                          },
                        }))
                      }
                      className='mt-1 w-full rounded-md border border-nova-input px-3 py-2 text-sm font-mono disabled:bg-nova-muted'
                    />
                  </label>

                  <div className='flex flex-wrap gap-2'>
                    <button
                      type='button'
                      disabled={alreadySent || saveDraftMutation.isPending}
                      onClick={() =>
                        saveDraftMutation.mutate({
                          contact_id: row.contact_id,
                          email: edit.email,
                          subject: edit.subject,
                          body: edit.body,
                        })
                      }
                      className='px-3 py-2 rounded-md border border-nova-input text-sm font-semibold text-nova-foreground hover:bg-nova-accent disabled:opacity-50'
                    >
                      {saveDraftMutation.isPending ? 'Saving…' : 'Save draft'}
                    </button>
                    <button
                      type='button'
                      disabled={!canSend || sendMutation.isPending}
                      onClick={() => {
                        const ok = window.confirm(
                          `Send this pitch to ${edit.email.trim()} now?`
                        );
                        if (!ok) return;
                        sendMutation.mutate({
                          contact_id: row.contact_id,
                          email: edit.email,
                          subject: edit.subject,
                          body: edit.body,
                        });
                      }}
                      className='px-3 py-2 rounded-md bg-nova-primary text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50'
                    >
                      {sendMutation.isPending ? 'Sending…' : 'Send now'}
                    </button>
                    <button
                      type='button'
                      disabled={alreadySent}
                      onClick={() =>
                        setEdits((prev) => ({
                          ...prev,
                          [row.contact_id]: {
                            email: row.email ?? '',
                            subject: row.default_subject,
                            body: row.default_body,
                          },
                        }))
                      }
                      className='px-3 py-2 rounded-md border border-nova-input text-sm text-nova-muted-foreground hover:bg-nova-accent disabled:opacity-50'
                    >
                      Reset to starter copy
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4'>
        <details
          className='group'
          onToggle={(event) => {
            if (event.currentTarget.open) {
              markRepliesOpened();
            }
          }}
        >
          <summary className='cursor-pointer list-none flex items-start justify-between gap-3'>
            <div>
              <h2 className='text-lg font-semibold text-nova-foreground inline-flex items-center gap-2'>
                Replies
                {hasUnreadReplies ? (
                  <AdminUnreadDot label='New press replies since you last opened Replies' />
                ) : null}
                {repliesQuery.data ? (
                  <span className='ml-2 text-sm font-normal text-nova-muted-foreground'>
                    ({repliesQuery.data.replies.length}
                    {repliesQuery.data.has_more ? '+' : ''})
                  </span>
                ) : null}
              </h2>
              <p className='mt-1 text-sm text-nova-muted-foreground'>
                Live read-through of Resend Inbound. Click a row to read and
                reply.
              </p>
            </div>
            <span className='shrink-0 text-xs font-semibold text-nova-muted-foreground group-open:hidden'>
              + Expand
            </span>
            <span className='shrink-0 text-xs font-semibold text-nova-muted-foreground hidden group-open:inline'>
              − Collapse
            </span>
          </summary>

          <div className='mt-4 space-y-3'>
            <div className='flex items-center justify-between gap-2'>
              <p className='text-xs text-nova-muted-foreground'>
                {repliesQuery.data?.fetched_at
                  ? `Fetched ${formatRelative(repliesQuery.data.fetched_at)} from Resend`
                  : 'Not fetched yet'}
              </p>
              <button
                type='button'
                onClick={() => repliesQuery.refetch()}
                disabled={repliesQuery.isFetching}
                className='px-2.5 py-1 text-xs font-semibold rounded border border-nova-input text-nova-foreground hover:bg-nova-accent disabled:opacity-50'
              >
                {repliesQuery.isFetching ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
            {repliesQuery.isError ? (
              <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                Couldn't load replies: {parseDetail(repliesQuery.error)}
              </div>
            ) : null}
            {repliesQuery.data?.error ? (
              <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                Resend returned an error: {repliesQuery.data.error}
              </div>
            ) : null}
            {repliesQuery.isLoading ? (
              <p className='text-sm text-nova-muted-foreground'>Loading...</p>
            ) : repliesQuery.data &&
              repliesQuery.data.replies.length === 0 &&
              !repliesQuery.data.error ? (
              <p className='text-sm text-nova-muted-foreground italic'>
                No replies yet. Once an editor replies, Resend will surface it
                here.
              </p>
            ) : repliesQuery.data ? (
              <ul className='divide-y divide-nova-border border border-nova-border rounded-md overflow-hidden'>
                {repliesQuery.data.replies.map((reply) => {
                  const matched = reply.matched_contact;
                  const youReplied = Boolean(reply.manual_reply_sent_at);
                  return (
                    <li key={reply.id}>
                      <button
                        type='button'
                        onClick={() => {
                          setOpenReplyMode('html');
                          setOpenReplyId(reply.id);
                        }}
                        className={`w-full text-left px-3 py-2 flex flex-col sm:flex-row sm:items-center sm:gap-3 ${
                          youReplied
                            ? 'bg-blue-50/80 hover:bg-blue-50'
                            : 'hover:bg-nova-accent'
                        }`}
                      >
                        <div className='min-w-0 flex-1'>
                          <div className='flex flex-wrap items-baseline gap-2'>
                            <span className='text-sm font-semibold text-nova-foreground truncate'>
                              {reply.from_email}
                            </span>
                            {youReplied ? (
                              <span className='inline-flex px-1.5 py-0.5 rounded-md bg-blue-100 text-blue-900 text-[10px] font-semibold uppercase tracking-wide'>
                                You replied
                              </span>
                            ) : null}
                            {matched ? (
                              <span className='inline-flex px-1.5 py-0.5 rounded-md bg-green-100 text-green-800 text-[10px] font-semibold uppercase tracking-wide'>
                                ✓ {matched.outlet_name}
                              </span>
                            ) : (
                              <span className='inline-flex px-1.5 py-0.5 rounded-md bg-nova-muted text-nova-muted-foreground text-[10px] font-semibold uppercase tracking-wide'>
                                no match
                              </span>
                            )}
                          </div>
                          <p className='text-sm text-nova-foreground truncate'>
                            {reply.subject || '(no subject)'}
                          </p>
                        </div>
                        <div className='shrink-0 text-right text-[11px] text-nova-muted-foreground'>
                          {formatRelative(reply.received_at)}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        </details>
      </section>

      <section className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4'>
        <details className='group'>
          <summary className='cursor-pointer list-none flex items-start justify-between gap-3'>
            <div>
              <h2 className='text-lg font-semibold text-nova-foreground'>
                Sends
                {sendsQuery.data ? (
                  <span className='ml-2 text-sm font-normal text-nova-muted-foreground'>
                    ({sendsQuery.data.sends.length}
                    {sendsQuery.data.has_more ? '+' : ''})
                  </span>
                ) : null}
              </h2>
              <p className='mt-1 text-sm text-nova-muted-foreground'>
                Live read-through of Resend Sent.
              </p>
            </div>
            <span className='shrink-0 text-xs font-semibold text-nova-muted-foreground group-open:hidden'>
              + Expand
            </span>
            <span className='shrink-0 text-xs font-semibold text-nova-muted-foreground hidden group-open:inline'>
              − Collapse
            </span>
          </summary>
          <div className='mt-4 space-y-3'>
            <div className='flex items-center justify-between gap-2'>
              <p className='text-xs text-nova-muted-foreground'>
                {sendsQuery.data?.fetched_at
                  ? `Fetched ${formatRelative(sendsQuery.data.fetched_at)} from Resend`
                  : 'Not fetched yet'}
              </p>
              <button
                type='button'
                onClick={() => sendsQuery.refetch()}
                disabled={sendsQuery.isFetching}
                className='px-2.5 py-1 text-xs font-semibold rounded border border-nova-input text-nova-foreground hover:bg-nova-accent disabled:opacity-50'
              >
                {sendsQuery.isFetching ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
            {sendsQuery.data?.error ? (
              <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                Resend returned an error: {sendsQuery.data.error}
              </div>
            ) : null}
            {sendsQuery.isLoading ? (
              <p className='text-sm text-nova-muted-foreground'>Loading...</p>
            ) : sendsQuery.data &&
              sendsQuery.data.sends.length === 0 &&
              !sendsQuery.data.error ? (
              <p className='text-sm text-nova-muted-foreground italic'>
                No sends yet on this Resend API key.
              </p>
            ) : sendsQuery.data ? (
              <ul className='divide-y divide-nova-border border border-nova-border rounded-md overflow-hidden'>
                {sendsQuery.data.sends.map((send) => {
                  const matched = send.matched_contact;
                  return (
                    <li key={send.id}>
                      <button
                        type='button'
                        onClick={() => {
                          setOpenSendMode('html');
                          setOpenSendId(send.id);
                        }}
                        className='w-full text-left px-3 py-2 hover:bg-nova-accent flex flex-col sm:flex-row sm:items-center sm:gap-3'
                      >
                        <div className='min-w-0 flex-1'>
                          <div className='flex flex-wrap items-baseline gap-2'>
                            <span className='text-sm font-semibold text-nova-foreground truncate'>
                              {send.to_emails[0] || '(no recipient)'}
                            </span>
                            {matched ? (
                              <span className='inline-flex px-1.5 py-0.5 rounded-md bg-green-100 text-green-800 text-[10px] font-semibold uppercase tracking-wide'>
                                ✓ {matched.outlet_name}
                              </span>
                            ) : (
                              <span className='inline-flex px-1.5 py-0.5 rounded-md bg-nova-muted text-nova-muted-foreground text-[10px] font-semibold uppercase tracking-wide'>
                                no match
                              </span>
                            )}
                            {send.last_event ? (
                              <span
                                className={`inline-flex px-1.5 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide ${sendEventBadgeClass(send.last_event)}`}
                              >
                                {send.last_event}
                              </span>
                            ) : null}
                          </div>
                          <p className='text-sm text-nova-foreground truncate'>
                            {send.subject || '(no subject)'}
                          </p>
                        </div>
                        <div className='shrink-0 text-right text-[11px] text-nova-muted-foreground'>
                          {formatRelative(send.sent_at)}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        </details>
      </section>

      <ReplyDialog
        open={!!openReplyId}
        replyId={openReplyId}
        loading={replyDetailQuery.isLoading}
        error={
          replyDetailQuery.isError ? parseDetail(replyDetailQuery.error) : null
        }
        reply={replyDetailQuery.data ?? null}
        mode={openReplyMode}
        onModeChange={setOpenReplyMode}
        onClose={() => setOpenReplyId(null)}
        sendReplyMutation={sendReplyMutation}
      />
      <SendDialog
        open={!!openSendId}
        loading={sendDetailQuery.isLoading}
        error={
          sendDetailQuery.isError ? parseDetail(sendDetailQuery.error) : null
        }
        send={sendDetailQuery.data ?? null}
        mode={openSendMode}
        onModeChange={setOpenSendMode}
        onClose={() => setOpenSendId(null)}
      />
    </div>
  );
}

function ReplyDialog({
  open,
  replyId,
  loading,
  error,
  reply,
  mode,
  onModeChange,
  onClose,
  sendReplyMutation,
}: {
  open: boolean;
  replyId: string | null;
  loading: boolean;
  error: string | null;
  reply: PressReplyDetailResponse | null;
  mode: 'html' | 'text';
  onModeChange: (m: 'html' | 'text') => void;
  onClose: () => void;
  sendReplyMutation: UseMutationResult<
    PressReplySendResponse,
    unknown,
    { replyId: string; payload: PressReplySendRequest }
  >;
}) {
  const [composing, setComposing] = useState(false);
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [includeQuoted, setIncludeQuoted] = useState(true);
  const [attachFiles, setAttachFiles] = useState<File[]>([]);
  const alreadyReplied = Boolean(reply?.manual_reply_sent_at);
  const sendLocked =
    alreadyReplied ||
    sendReplyMutation.isSuccess ||
    sendReplyMutation.isPending;

  useEffect(() => {
    if (!reply) return;
    setReplySubject(dedupeReplySubject(reply.subject));
    setReplyBody('');
    setIncludeQuoted(true);
    setAttachFiles([]);
    setComposing(false);
  }, [reply?.id]);

  if (!open) return null;

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'
      onClick={onClose}
    >
      <div
        className='bg-nova-card rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col'
        onClick={(event) => event.stopPropagation()}
      >
        <header className='flex items-start justify-between gap-4 border-b border-nova-border px-5 py-4'>
          <div className='min-w-0'>
            <h2 className='text-lg font-semibold text-nova-foreground'>
              Reply
              {reply?.matched_contact ? (
                <span className='ml-2 text-sm font-normal text-nova-muted-foreground'>
                  from {reply.matched_contact.outlet_name}
                </span>
              ) : null}
            </h2>
            {reply?.received_at ? (
              <p className='mt-0.5 text-xs text-nova-muted-foreground'>
                Received {formatRelative(reply.received_at)} ·{' '}
                {formatTimestamp(reply.received_at)}
              </p>
            ) : null}
          </div>
          <div className='flex shrink-0 items-center gap-2'>
            {reply && !alreadyReplied ? (
              <button
                type='button'
                onClick={() => setComposing((value) => !value)}
                disabled={sendLocked}
                className='rounded-md border border-nova-primary bg-nova-primary px-3 py-1 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50'
              >
                {composing ? 'Hide composer' : 'Reply'}
              </button>
            ) : null}
            <button
              type='button'
              onClick={onClose}
              className='rounded-md border border-nova-input px-2 py-1 text-sm text-nova-foreground hover:bg-nova-accent'
            >
              Close
            </button>
          </div>
        </header>
        {loading ? (
          <div className='p-4 text-sm text-nova-muted-foreground'>Loading reply...</div>
        ) : error ? (
          <div className='m-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
            {error}
          </div>
        ) : reply ? (
          <>
            <div className='border-b border-nova-border bg-nova-muted px-5 py-3 text-xs text-nova-foreground'>
              <div>
                <span className='font-semibold text-nova-muted-foreground'>From </span>
                {reply.from_email}
              </div>
              <div className='mt-1'>
                <span className='font-semibold text-nova-muted-foreground'>Subject </span>
                {reply.subject || '(no subject)'}
              </div>
            </div>
            {composing ? (
              <div className='border-b border-nova-border bg-nova-card px-5 py-4 space-y-3'>
                <input
                  type='text'
                  value={replySubject}
                  onChange={(event) => setReplySubject(event.target.value)}
                  disabled={sendLocked}
                  className='w-full rounded-md border border-nova-input px-2 py-1.5 text-sm disabled:bg-nova-muted'
                />
                <textarea
                  rows={8}
                  value={replyBody}
                  onChange={(event) => setReplyBody(event.target.value)}
                  disabled={sendLocked}
                  className='w-full rounded-md border border-nova-input px-2 py-1.5 text-sm font-mono disabled:bg-nova-muted'
                />
                <label className='flex items-center gap-2 text-sm text-nova-foreground'>
                  <input
                    type='checkbox'
                    checked={includeQuoted}
                    onChange={(event) => setIncludeQuoted(event.target.checked)}
                    disabled={sendLocked}
                  />
                  Include quoted original below
                </label>
                <OutreachReplyAttachmentPicker
                  files={attachFiles}
                  onChange={setAttachFiles}
                  disabled={sendLocked}
                />
                {sendReplyMutation.isError ? (
                  <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                    {parseDetail(sendReplyMutation.error)}
                  </div>
                ) : null}
                {sendReplyMutation.isSuccess && sendReplyMutation.data ? (
                  <div className='rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900'>
                    Sent {formatTimestamp(sendReplyMutation.data.sent_at)}.
                  </div>
                ) : null}
                <div className='flex justify-end'>
                  <button
                    type='button'
                    disabled={sendLocked || replyBody.trim().length === 0}
                    onClick={() => {
                      if (!replyId) return;
                      sendReplyMutation.mutate({
                        replyId,
                        payload: {
                          subject:
                            replySubject.trim() ||
                            dedupeReplySubject(reply.subject),
                          body: replyBody.trim(),
                          include_quoted_original: includeQuoted,
                          files: attachFiles,
                        },
                      });
                    }}
                    className='rounded-md bg-nova-primary px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50'
                  >
                    {sendReplyMutation.isPending ? 'Sending…' : 'Send reply'}
                  </button>
                </div>
              </div>
            ) : null}
            <div className='border-b border-nova-border px-5 pt-3'>
              <div className='inline-flex rounded-md border border-nova-input overflow-hidden text-sm'>
                <button
                  type='button'
                  onClick={() => onModeChange('html')}
                  disabled={!reply.html}
                  className={`px-3 py-1.5 ${
                    mode === 'html'
                      ? 'bg-nova-primary text-white'
                      : 'bg-nova-card text-nova-foreground'
                  }`}
                >
                  HTML
                </button>
                <button
                  type='button'
                  onClick={() => onModeChange('text')}
                  disabled={!reply.text}
                  className={`px-3 py-1.5 border-l border-nova-input ${
                    mode === 'text'
                      ? 'bg-nova-primary text-white'
                      : 'bg-nova-card text-nova-foreground'
                  }`}
                >
                  Plain text
                </button>
              </div>
            </div>
            <div className='flex-1 min-h-0 overflow-auto bg-nova-muted p-4'>
              {mode === 'html' && reply.html ? (
                <iframe
                  title='Reply HTML'
                  srcDoc={reply.html}
                  sandbox=''
                  className='w-full h-[min(40vh,360px)] bg-nova-card rounded shadow border border-nova-border'
                />
              ) : reply.text ? (
                <pre className='w-full bg-nova-card rounded shadow border border-nova-border p-4 text-sm font-mono whitespace-pre-wrap text-nova-foreground'>
                  {reply.text}
                </pre>
              ) : (
                <p className='text-sm text-nova-muted-foreground italic'>
                  No body returned by Resend for this reply.
                </p>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function SendDialog({
  open,
  loading,
  error,
  send,
  mode,
  onModeChange,
  onClose,
}: {
  open: boolean;
  loading: boolean;
  error: string | null;
  send: PressSendDetailResponse | null;
  mode: 'html' | 'text';
  onModeChange: (m: 'html' | 'text') => void;
  onClose: () => void;
}) {
  if (!open) return null;
  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'
      onClick={onClose}
    >
      <div
        className='bg-nova-card rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col'
        onClick={(event) => event.stopPropagation()}
      >
        <header className='flex items-start justify-between gap-4 border-b border-nova-border px-5 py-4'>
          <h2 className='text-lg font-semibold text-nova-foreground'>Sent email</h2>
          <button
            type='button'
            onClick={onClose}
            className='rounded-md border border-nova-input px-2 py-1 text-sm text-nova-foreground hover:bg-nova-accent'
          >
            Close
          </button>
        </header>
        {loading ? (
          <div className='p-4 text-sm text-nova-muted-foreground'>Loading send...</div>
        ) : error ? (
          <div className='m-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
            {error}
          </div>
        ) : send ? (
          <>
            <div className='border-b border-nova-border bg-nova-muted px-5 py-3 text-xs text-nova-foreground'>
              <div>To {send.to_emails.join(', ') || '—'}</div>
              <div className='mt-1'>{send.subject || '(no subject)'}</div>
            </div>
            <div className='border-b border-nova-border px-5 pt-3'>
              <div className='inline-flex rounded-md border border-nova-input overflow-hidden text-sm'>
                <button
                  type='button'
                  onClick={() => onModeChange('html')}
                  className={`px-3 py-1.5 ${
                    mode === 'html'
                      ? 'bg-nova-primary text-white'
                      : 'bg-nova-card text-nova-foreground'
                  }`}
                >
                  HTML
                </button>
                <button
                  type='button'
                  onClick={() => onModeChange('text')}
                  className={`px-3 py-1.5 border-l border-nova-input ${
                    mode === 'text'
                      ? 'bg-nova-primary text-white'
                      : 'bg-nova-card text-nova-foreground'
                  }`}
                >
                  Plain text
                </button>
              </div>
            </div>
            <div className='flex-1 min-h-0 overflow-auto bg-nova-muted p-4'>
              {mode === 'html' && send.html ? (
                <iframe
                  title='Send HTML'
                  srcDoc={send.html}
                  sandbox=''
                  className='w-full h-[60vh] bg-nova-card rounded shadow border border-nova-border'
                />
              ) : send.text ? (
                <pre className='w-full bg-nova-card rounded shadow border border-nova-border p-4 text-sm font-mono whitespace-pre-wrap text-nova-foreground'>
                  {send.text}
                </pre>
              ) : (
                <p className='text-sm text-nova-muted-foreground italic'>
                  No body returned by Resend for this send.
                </p>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/press-outreach')({
  component: PressOutreachPage,
});
